import type { SVGProps } from "react";

type IconName =
  | "arrow-left"
  | "share"
  | "copy"
  | "wifi"
  | "wifi-off"
  | "flag"
  | "handshake"
  | "rotate"
  | "plus"
  | "clock"
  | "link"
  | "user"
  | "install"
  | "check"
  | "x"
  | "eye"
  | "bell"
  | "volume"
  | "volume-off"
  | "message"
  | "send"
  | "mic"
  | "play"
  | "pause"
  | "stop"
  | "trash"
  | "ios-share"
  | "add-square"
  | "chevron";

const paths: Record<IconName, React.ReactNode> = {
  "arrow-left": <><path d="m15 18-6-6 6-6" /><path d="M9 12h10" /></>,
  share: <><circle cx="18" cy="5" r="2.5" /><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="19" r="2.5" /><path d="m8.2 10.8 7.6-4.5M8.2 13.2l7.6 4.5" /></>,
  copy: <><rect width="13" height="13" x="8" y="8" rx="2" /><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" /></>,
  wifi: <><path d="M5 12.6a10 10 0 0 1 14 0M8.5 16a5 5 0 0 1 7 0" /><circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" /></>,
  "wifi-off": <><path d="m3 3 18 18M8.5 16a5 5 0 0 1 5.1-.9M5 12.6A10 10 0 0 1 9.5 10M14.5 10a10 10 0 0 1 4.5 2.6" /><circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" /></>,
  flag: <><path d="M5 21V4M5 5c5-4 8 3 14-1v10c-6 4-9-3-14 1" /></>,
  handshake: <><path d="m8 11 2 2c1 1 2.5 1 3.5 0l3-3M3 8l4-3 4 2M21 8l-4-3-4 2" /><path d="m4 9 5 8c1 1 2 1 3 0l1-1c1 1 2 1 3 0l4-7" /></>,
  rotate: <><path d="M20 7v5h-5M4 17v-5h5" /><path d="M18.5 9A7 7 0 0 0 6 6.5L4 9M5.5 15A7 7 0 0 0 18 17.5l2-2.5" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  link: <><path d="M10 13a4 4 0 0 0 5.7.1l2.4-2.4a4 4 0 0 0-5.7-5.7L11 6.4" /><path d="M14 11a4 4 0 0 0-5.7-.1l-2.4 2.4A4 4 0 0 0 11.6 19l1.4-1.4" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4.5 21a7.5 7.5 0 0 1 15 0" /></>,
  install: <><path d="M12 3v12m0 0 4-4m-4 4-4-4" /><path d="M5 17v3h14v-3" /></>,
  check: <path d="m5 12 4 4L19 6" />,
  x: <path d="M6 6l12 12M18 6 6 18" />,
  eye: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></>,
  bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 21h4" /></>,
  volume: <><path d="M11 5 6 9H3v6h3l5 4Z" /><path d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8.5 8.5 0 0 1 0 12" /></>,
  "volume-off": <><path d="M11 5 6 9H3v6h3l5 4Z" /><path d="m16 10 5 5M21 10l-5 5" /></>,
  message: <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />,
  send: <><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></>,
  mic: <><rect x="9" y="3" width="6" height="12" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3M8 21h8" /></>,
  play: <path d="m9 7 8 5-8 5Z" />,
  pause: <><path d="M9 7v10M15 7v10" /></>,
  stop: <rect x="8" y="8" width="8" height="8" rx="1" />,
  trash: <><path d="M4 7h16M9 7V4h6v3M7 7l1 14h8l1-14M10 11v6M14 11v6" /></>,
  "ios-share": <><path d="M12 15V3M8 7l4-4 4 4" /><path d="M5 10v10h14V10" /></>,
  "add-square": <><rect x="3" y="3" width="18" height="18" rx="4" /><path d="M12 8v8M8 12h8" /></>,
  chevron: <path d="m9 18 6-6-6-6" />,
};

export function Icon({ name, ...props }: { name: IconName } & SVGProps<SVGSVGElement>) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      {paths[name]}
    </svg>
  );
}
